import { useEffect, useState } from "react";
import { ArrowUpRight } from "@phosphor-icons/react";
import ProductCard from "@/pages/shop/ProductCard";
import ProductModal from "@/pages/shop/ProductModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * KMS Featured Strip — renders the 2 KMS-exclusive pieces (Tee + Polo)
 * underneath the signup form on `/kms`. Clicking any card or the
 * "Shop the full drop" CTA routes to `/kms/shop` where the full catalog
 * shows below the featured pair.
 *
 * Mirrors the "KMS × Sweat With Slay" hero section on `/kms/shop` so the
 * two pages feel like one continuous flow.
 */
export default function KmsFeaturedStrip() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${BACKEND}/api/shop/products?include_kms=1`)
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then((d) => {
        if (!alive) return;
        const kms = (d.products || []).filter((p) => p.kms_exclusive);
        // Tee first, polo second
        kms.sort((a, b) => {
          const rank = (n) => {
            const s = (n || "").toLowerCase();
            if (/(t-?shirt|\btee\b)/.test(s)) return 0;
            if (/polo|golf/.test(s)) return 1;
            return 2;
          };
          return rank(a.name) - rank(b.name);
        });
        setProducts(kms);
      })
      .catch((err) => console.warn("KmsFeaturedStrip: fetch failed:", err))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading || products.length === 0) return null;

  return (
    <section
      data-testid="kms-featured-strip"
      className="px-5 md:px-12 py-14 md:py-24 border-t border-b border-white/10 bg-[#0F0F0F] text-white"
    >
      {/* Same red/white/blue banner as /kms/shop so the pages feel unified */}
      <div className="bg-gradient-to-r from-[#003366] via-[#0A0A0A] to-[#B32027] -mx-5 md:-mx-12 -mt-14 md:-mt-24 mb-10 md:mb-14 px-5 md:px-12 py-5 border-b border-white/20">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4">
          <div className="text-[11px] md:text-[12px] uppercase tracking-[0.3em] font-bold text-white">
            KMS × Sweat With Slay · Exclusive team drop
          </div>
          <span className="text-white/75 text-[12px] md:text-sm leading-relaxed">
            Team-only pieces below · every regular product also available
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-6 md:mb-8 flex-wrap gap-4">
          <div>
            <p className="text-[#FF1B91] uppercase tracking-[0.35em] text-[10px] font-bold mb-2 md:mb-3">
              / KMS Technology · Team-only drop
            </p>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-7xl uppercase tracking-tighter leading-[0.85]">
              KMS × Sweat<br />With Slay.
            </h2>
            <p className="text-white/70 text-sm md:text-lg mt-3 md:mt-4 max-w-xl leading-relaxed">
              Two exclusive pieces for KMS Technology employees. Not listed on the public shop.
            </p>
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/45">
            {products.length} piece{products.length === 1 ? "" : "s"}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 md:gap-5">
          {products.map((p) => (
            <ProductCard
              key={p.id}
              p={p}
              onOpen={(x) => setSelectedId(x.id)}
            />
          ))}
        </div>

        <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-3">
          <a
            href="/kms/shop"
            data-testid="kms-featured-shop-all"
            className="inline-flex items-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.25em] transition-colors"
          >
            Shop the full drop <ArrowUpRight size={12} weight="bold" />
          </a>
          <span className="text-white/60 text-[11px] uppercase tracking-[0.25em]">
            → Tap a piece to see details · full catalog also available
          </span>
        </div>
      </div>

      {/* Full ProductModal — same experience as /shop so tapping the tile
          actually opens the product (fixes "clicking the KMS tile doesn't
          show the product" on mobile). */}
      {selectedId && (
        <ProductModal productId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </section>
  );
}
